import { useState, useEffect } from "react";
import Modal from 'react-bootstrap/Modal';
import posthog from 'posthog-js';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Image from 'react-bootstrap/Image';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Accordion from 'react-bootstrap/Accordion';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useNavigate } from "react-router-dom";
import httpService from "../../services/httpService";

import { FaRegLightbulb } from "react-icons/fa";
import { FaRegClock } from "react-icons/fa";
import { FaRocketchat } from "react-icons/fa";
import { FaLock } from "react-icons/fa";
import { FaLockOpen } from "react-icons/fa";

import "../../styles/ClubDetailsModal.scss";
import "../../styles/dashbaords.scss";

const OnboardingModalInvite = ({ show, onHide, invite }) => {
    const [clubDetails, setClubDetails] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [loadingClub, setLoadingClub] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [subscribing, setSubscribing] = useState(false);
    const [subscribeError, setSubscribeError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (show && invite) {
            // Reset state before loading
            setClubDetails(null);
            setIsSubscribed(false);
            setLoadError(null);
            setSubscribeError(null);
            setLoadingClub(true);

            httpService.get(`/dash/listener-podcast-club/${invite.podcast_club.guid}`)
                .then((res) => {
                    setClubDetails(res.data.podcast_club);
                    setIsSubscribed(res.data.is_subscribed);
                })
                .catch((err) => {
                    console.error("Error loading invited club details:", err);
                    setLoadError("Could not load club details. Please close and try again.");
                })
                .finally(() => setLoadingClub(false));
        } else {
            // Reset on close so next open has no stale state
            setClubDetails(null);
            setIsSubscribed(false);
            setLoadError(null);
            setSubscribeError(null);
        }
    }, [show, invite]);

    const handleRedeemInvite = () => {
        if (!invite?.access_code) { onHide(); return; }

        setSubscribing(true);
        setSubscribeError(null);

        httpService.post("invite/redeem", { code: invite.access_code })
            .then(async (res) => {
                const data = res.data?.data;

                if (data?.deferred) {
                    // User is authenticated but has no Listener profile yet — create it first,
                    // which auto-subscribes them via the stored signup_token flow.
                    await httpService.post("dash/register-demographics", { role: 'listener' });
                }

                posthog.capture('listener_club_joined', {
                    club_guid: invite.podcast_club.guid,
                    club_title: clubDetails?.title ?? invite.podcast_club?.title ?? null,
                });
                navigate('/listener-podcast-club/' + invite.podcast_club.guid);
            })
            .catch((err) => {
                console.error("Error redeeming invite:", err);
                setSubscribeError(err.response?.data?.message || "Failed to join the club. Please try again.");
            })
            .finally(() => setSubscribing(false));
    };

    return (
        <Modal id="onboarding-invite" show={show} onHide={handleRedeemInvite} size="xl" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Row className="align-items-center w-100">
                    <Col xs={2}>
                        <Image src="/img/podsy-logo-w-200.png" fluid />
                    </Col>
                    <Col xs={10}>
                        <h4 className="mb-0">You're in. This is : </h4>
                        <h1 className="mb-0">
                            {clubDetails?.title ?? invite?.podcast_club?.title ?? "a Podcast Club"}
                        </h1>
                    </Col>
                </Row>
            </Modal.Header>

            <Modal.Body>
                {loadingClub && (
                    <div className="text-center my-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading club details...</span>
                        </Spinner>
                    </div>
                )}

                {!loadingClub && loadError && (
                    <Alert variant="danger">{loadError}</Alert>
                )}

                {!loadingClub && clubDetails && (
                    <Container>
                        {/* Header image */}
                        <Row className="mb-3">
                            <Col>
                                <Image
                                    id="club-header-image"
                                    className="header-image"
                                    src={clubDetails.super_wide_image ?? '/img/club-defaults/super-wide.jpg'}
                                    fluid
                                />
                            </Col>
                        </Row>

                        {/* Subscribe button + metadata icons */}
                        <Row className="mb-3">
                            <Col sm={12}>
                                <p>
                                    Each week we anchor to one episode. Each week I post a question tied to the episode. You reply when you're ready, and you can read what other listeners are working through too, and a set of things you can try on your own…. privately, nobody else sees what you're working on unless you tell them.
                                </p>
                                <p>
                                    I read everything that gets posted here. If something you share stays with me, I'll reach out directly.
                                </p>
                                <p>
                                    Your first episode is <strong>{clubDetails.episodes?.[0]?.pivot?.title || clubDetails.episodes?.[0]?.title || 'the first episode'}</strong>. Head in when you're ready.
                                </p>
                                <p>{invite.inviter?.first_name} {invite.inviter?.last_name}</p>
                            </Col>
                        </Row>
                    </Container>
                )}
            </Modal.Body>

            {subscribeError && (
                <Modal.Footer className="justify-content-start">
                    <Alert variant="danger" className="w-100 mb-0" onClose={() => setSubscribeError(null)} dismissible>
                        {subscribeError}
                    </Alert>
                </Modal.Footer>
            )}

            <Modal.Footer>
                <Button variant="primary" onClick={handleRedeemInvite} disabled={subscribing}>
                    {subscribing ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Joining...
                        </>
                    ) : 'Take me to Week 1'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default OnboardingModalInvite;
